'use client';

import React from 'react';

/**
 * Watch for changes being made to the DOM tree.
 *
 * @param target - React ref or DOM node
 * @param callback - callback to execute when mutations are observed
 * @param options - Options passed to mutation observer
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver MutationObserver MDN
 */

export interface UseMutationObserver {
  target: React.Ref<HTMLElement | null>;
  callback: MutationCallback;
  options?: MutationObserverInit & { isSupported: boolean; stop: () => void };
}

export function useMutationObserver(
  ref: React.RefObject<HTMLElement>,
  callback: MutationCallback,
  options = {
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  },
) {
  React.useEffect(() => {
    if (!ref?.current) return;
    const observer = new MutationObserver(callback);
    observer.observe(ref.current, options);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, options]);
}
